import { Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Container } from '@/components/generals/container'
import { cn } from '@/lib/utils'

import {
    Card,
    CardContent,
    CardDescription,
    CardTitle
} from '@/components/ui/card'

import { LOGO_EXPORT_PRICE_LABEL } from '@/constants/exports'
import { unlimitedMonthlyPrice, unlimitedYearlyPrice } from '@/constants/site'

interface PricingPlansProps {
    interval: 'monthly' | 'yearly'
    isSubscriber?: boolean
    loadingPlan?: 'logo' | 'paid' | null
    onBuyLogo: () => void
    onSubscribe: () => void
    onManage?: () => void
    onIntervalChange: (interval: 'monthly' | 'yearly') => void
}

const SINGLE_FEATURES = [
    'Three concepts designed from your brief',
    'Full-resolution PNG, no watermark',
    'SVG, PDF and EPS vector files',
    'Full commercial use, yours forever',
    'One payment, nothing recurring'
]

const UNLIMITED_FEATURES = [
    'Unlimited logo designs, three concepts each',
    'Every file format on every logo',
    'Private logos, kept out of the gallery',
    'Cancel anytime from your account'
]

export function PricingPlans({
    interval,
    isSubscriber = false,
    loadingPlan = null,
    onBuyLogo,
    onSubscribe,
    onManage,
    onIntervalChange
}: PricingPlansProps) {
    const isYearly = interval === 'yearly'
    const price = `$${isYearly ? unlimitedYearlyPrice : unlimitedMonthlyPrice}`
    const per = isYearly ? 'per year' : 'per month'
    const yearlySaving = unlimitedMonthlyPrice * 12 - unlimitedYearlyPrice
    const savingPercent = Math.round((yearlySaving / (unlimitedMonthlyPrice * 12)) * 100)

    const toggleClass = (active: boolean) =>
        cn(
            'flex-1 rounded-full px-4 py-2 text-small font-medium transition-colors duration-150 ease-out',
            active ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
        )

    return (
        <Container width='content'>
            <div className='grid gap-6 md:grid-cols-2'>
                {/* Single logo: the offer that carries the page. */}
                <Card className='flex flex-col border-line-strong'>
                    <div className='flex flex-1 flex-col gap-7 p-8 md:p-10'>
                        <div className='flex flex-wrap items-center gap-3'>
                            <span className='type-eyebrow'>Pay per logo</span>
                            <Badge variant='signal'>Most people start here</Badge>
                        </div>

                        <div className='flex flex-col gap-3'>
                            <h2 className='font-display text-h2 text-balance'>Single logo</h2>
                            <p className='text-body text-ink-muted text-pretty'>
                                Pay once and we design three concepts from your brief, with every
                                file unlocked and nothing to renew.
                            </p>
                        </div>

                        <div className='flex items-baseline gap-2'>
                            <span className='font-display text-h1'>{LOGO_EXPORT_PRICE_LABEL}</span>
                            <span className='text-small text-ink-muted'>one time, per logo</span>
                        </div>

                        <ul className='flex flex-col gap-3 border-t border-line pt-6'>
                            {SINGLE_FEATURES.map((feature) => (
                                <li key={feature} className='flex items-start gap-3'>
                                    <Check className='mt-1 size-4 shrink-0 text-ink' aria-hidden />
                                    <span className='text-body text-ink'>{feature}</span>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-auto flex flex-col gap-3 pt-2'>
                            <Button
                                onClick={onBuyLogo}
                                variant='signal'
                                size='lg'
                                className='w-full'
                                disabled={loadingPlan === 'logo'}
                            >
                                {loadingPlan === 'logo' ? 'Loading…' : 'Make a logo'}
                            </Button>
                            <p className='text-center text-caption text-ink-subtle'>
                                Secure checkout by Stripe, before the design starts.
                            </p>
                        </div>
                    </div>
                </Card>

                {/* Unlimited, with its own monthly or yearly switch. */}
                <Card className='flex flex-col'>
                    <div className='flex flex-1 flex-col gap-7 p-8 md:p-10'>
                        <div className='flex flex-col gap-2'>
                            <span className='type-eyebrow'>For repeat users</span>
                            <CardTitle className='font-display text-h2'>Unlimited</CardTitle>
                            <CardDescription>
                                For agencies, freelancers and anyone making logos every week
                            </CardDescription>
                        </div>

                        <div
                            role='group'
                            aria-label='Billing interval'
                            className='flex items-center rounded-full border border-line bg-surface-2 p-1'
                        >
                            <button
                                type='button'
                                aria-pressed={!isYearly}
                                onClick={() => onIntervalChange('monthly')}
                                className={toggleClass(!isYearly)}
                            >
                                Monthly
                            </button>
                            <button
                                type='button'
                                aria-pressed={isYearly}
                                onClick={() => onIntervalChange('yearly')}
                                className={toggleClass(isYearly)}
                            >
                                Yearly
                                <span className='ml-1.5 text-success'>Save {savingPercent}%</span>
                            </button>
                        </div>

                        <div className='flex flex-col gap-1'>
                            <div className='flex items-baseline gap-2'>
                                <span className='font-display text-h1'>{price}</span>
                                <span className='text-small text-ink-muted'>{per}</span>
                            </div>
                            <p className='text-small text-ink-muted'>
                                {isYearly
                                    ? `About $${Math.round(unlimitedYearlyPrice / 12)} a month, saving $${yearlySaving} a year`
                                    : 'Pays for itself from the third logo in a month'}
                            </p>
                        </div>

                        <CardContent className='flex flex-col gap-3 border-t border-line p-0 pt-6'>
                            {UNLIMITED_FEATURES.map((feature) => (
                                <div key={feature} className='flex items-start gap-3'>
                                    <Check className='mt-1 size-4 shrink-0 text-ink-subtle' aria-hidden />
                                    <span className='text-body text-ink-muted'>{feature}</span>
                                </div>
                            ))}
                        </CardContent>

                        <div className='mt-auto pt-2'>
                            {isSubscriber ? (
                                <Button onClick={onManage} variant='outline' size='lg' className='w-full'>
                                    Manage plan
                                </Button>
                            ) : (
                                <Button
                                    onClick={onSubscribe}
                                    variant='outline'
                                    size='lg'
                                    className='w-full'
                                    disabled={loadingPlan === 'paid'}
                                >
                                    {loadingPlan === 'paid'
                                        ? 'Loading…'
                                        : `Subscribe ${isYearly ? 'yearly' : 'monthly'}`}
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </Container>
    )
}
